import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getPrograms, getUniversities, getCategories } from '../services/api';
import { EdFiLogo } from '../components/EdFiLogo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Filter, Clock, DollarSign, GraduationCap, Building2, ArrowRight, ArrowLeft } from 'lucide-react';

const ProgramsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [programs, setPrograms] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    university_id: searchParams.get('university') || '',
    category: searchParams.get('category') || '',
    modality: searchParams.get('modality') || ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [uniRes, catRes] = await Promise.all([
          getUniversities(),
          getCategories()
        ]);
        setUniversities(uniRes.data);
        setCategories(catRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchPrograms = async () => {
      setLoading(true);
      try {
        const params = {};
        if (filters.search) params.search = filters.search;
        if (filters.university_id) params.university_id = filters.university_id;
        if (filters.category) params.category = filters.category;
        if (filters.modality) params.modality = filters.modality;

        const response = await getPrograms(params);
        setPrograms(response.data.programs);
        setTotal(response.data.total);
      } catch (error) {
        console.error('Error fetching programs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPrograms();
  }, [filters]);

  const handleFilterChange = (key, value) => {
    const actualValue = value === 'all' ? '' : value;
    const newFilters = { ...filters, [key]: actualValue };
    setFilters(newFilters);
    
    const params = new URLSearchParams();
    if (newFilters.search) params.set('search', newFilters.search);
    if (newFilters.university_id) params.set('university', newFilters.university_id);
    if (newFilters.category) params.set('category', newFilters.category);
    if (newFilters.modality) params.set('modality', newFilters.modality);
    setSearchParams(params);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getModalityLabel = (modality) => {
    const labels = {
      'online': 'En línea',
      'presencial': 'Presencial',
      'hibrido': 'Híbrido',
      'virtual': 'Virtual'
    };
    return labels[modality] || modality;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-8">
              <EdFiLogo size="sm" linkTo="/" />
              <Link to="/" className="text-slate-600 hover:text-brand-blue transition-colors flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" />
                Inicio
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/calculator">
                <Button variant="outline" className="border-brand-blue text-brand-blue">Calculadora</Button>
              </Link>
              <Link to="/login">
                <Button className="bg-brand-magenta hover:bg-brand-magenta-dark text-white">Aplicar Ahora</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <div className="bg-brand-blue py-16">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-4xl lg:text-5xl font-black text-white tracking-tight mb-4">
            Catalogo Educativo
          </h1>
          <p className="text-xl text-white/80 max-w-2xl">
            Explora programas de las mejores universidades de Mexico con financiamiento disponible
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-slate-200 py-6">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Buscar programa..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={filters.university_id || 'all'} onValueChange={(v) => handleFilterChange('university_id', v)}>
              <SelectTrigger data-testid="university-filter">
                <SelectValue placeholder="Universidad" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las universidades</SelectItem>
                {universities.map((uni) => (
                  <SelectItem key={uni.university_id} value={uni.university_id}>
                    {uni.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.category || 'all'} onValueChange={(v) => handleFilterChange('category', v)}>
              <SelectTrigger data-testid="category-filter">
                <SelectValue placeholder="Categoría" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.modality || 'all'} onValueChange={(v) => handleFilterChange('modality', v)}>
              <SelectTrigger data-testid="modality-filter">
                <SelectValue placeholder="Modalidad" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las modalidades</SelectItem>
                <SelectItem value="online">En línea</SelectItem>
                <SelectItem value="presencial">Presencial</SelectItem>
                <SelectItem value="hibrido">Híbrido</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <p className="text-slate-600">
            <span className="font-semibold text-slate-900">{total}</span> programas encontrados
          </p>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 p-6 animate-pulse">
                <div className="h-6 bg-slate-200 rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-slate-200 rounded w-1/2 mb-2"></div>
                <div className="h-4 bg-slate-200 rounded w-full mb-4"></div>
                <div className="h-10 bg-slate-200 rounded w-full"></div>
              </div>
            ))}
          </div>
        ) : programs.length === 0 ? (
          <div className="text-center py-16">
            <GraduationCap className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No se encontraron programas</h3>
            <p className="text-slate-600">Intenta ajustar los filtros de búsqueda</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {programs.map((program) => (
              <div
                key={program.program_id}
                className="bg-white rounded-xl border border-slate-200 p-6 card-hover"
                data-testid={`program-card-${program.program_id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-brand-blue/10 text-brand-blue">
                    {program.category}
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                    {getModalityLabel(program.modality)}
                  </span>
                </div>
                
                <h3 className="text-lg font-bold text-slate-900 mb-2 line-clamp-2">
                  {program.name}
                </h3>
                
                <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
                  <Building2 className="w-4 h-4" />
                  <span>{program.university_name}</span>
                </div>
                
                {program.description && (
                  <p className="text-sm text-slate-600 mb-4 line-clamp-2">{program.description}</p>
                )}
                
                <div className="flex items-center gap-4 text-sm text-slate-600 mb-4">
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>{program.duration_months} meses</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <DollarSign className="w-4 h-4" />
                    <span className="font-semibold text-slate-900">{formatPrice(program.price)}</span>
                  </div>
                </div>
                
                <Link to={`/programs/${program.program_id}`}>
                  <Button className="w-full bg-brand-blue hover:bg-brand-blue/90 text-white" data-testid={`view-program-${program.program_id}`}>
                    Ver Programa
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgramsPage;
